/* eslint-disable @angular-eslint/no-output-on-prefix, @typescript-eslint/no-inferrable-types */
import { animate, animateChild, AnimationEvent, query, state, style, transition, trigger } from '@angular/animations'
import { CommonModule } from '@angular/common'
import {
  AfterContentInit,
  AfterViewInit,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  NgModule,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
} from '@angular/core'
import { PrimeTemplate, SharedModule, Message } from 'primeng/api'
import { DomHandler } from 'primeng/dom'
import { Subscription } from 'rxjs'
import { DocumentRefService } from '../globals-services/document-ref.service'
import { MessageService } from './messageservice'
import { ButtonModule } from 'primeng/button'

@Component({
  selector: 'app-toast-item',
  template: `
    <div
      #container
      class="p-toast-message"
      [@messageState]="{
        value: 'visible',
        params: { showTransitionParams: showTransitionOptions, hideTransitionParams: hideTransitionOptions }
      }"
      [ngClass]="{
        'p-toast-message-info': message.severity === 'info',
        'p-toast-message-warn': message.severity === 'warn',
        'p-toast-message-error': message.severity === 'error',
        'p-toast-message-success': message.severity === 'success'
      }"
      (mouseenter)="onMouseEnter()"
      (mouseleave)="onMouseLeave()"
    >
      <div class="p-toast-message-content">
        <button
          *ngIf="message.closable !== false"
          pButton
          icon="pi pi-times"
          class="p-toast-close-icon p-button-rounded p-button-text"
          i18n-aria-label
          aria-label="Close"
          (click)="onCloseIconClick($event)"
        ></button>
        <ng-container *ngIf="!template">
          <span
            class="p-toast-icon pi"
            [ngClass]="{
              'pi-info-circle': message.severity === 'info',
              'pi-exclamation-triangle': message.severity === 'warn',
              'pi-times': message.severity === 'error',
              'pi-check': message.severity === 'success'
            }"
          ></span>
          <div class="p-toast-message-text-content">
            <div class="p-toast-summary">{{ message.summary }}</div>
            <div class="p-toast-detail">{{ message.detail }}</div>
          </div>
        </ng-container>
        <ng-container *ngTemplateOutlet="template; context: { $implicit: message }"></ng-container>
      </div>
    </div>
  `,
  animations: [
    trigger('messageState', [
      state(
        'visible',
        style({
          transform: 'translateY(0)',
          opacity: 1,
        }),
      ),
      transition('void => *', [
        style({ transform: 'translateY(100%)', opacity: 0 }),
        animate('{{showTransitionParams}}'),
      ]),
      transition('* => void', [
        animate(
          '{{hideTransitionParams}}',
          style({
            height: 0,
            opacity: 0,
            transform: 'translateY(-100%)',
          }),
        ),
      ]),
    ]),
  ],
})
export class ToastItemComponent implements AfterViewInit, OnDestroy {
  @Input() message: Message

  @Input() index: number

  @Input() template: TemplateRef<{ $implicit: Message }>

  @Input() showTransitionOptions: string

  @Input() hideTransitionOptions: string

  @Output() onClose = new EventEmitter<{
    index: number
    message: Message
  }>()

  @ViewChild('container') containerViewChild: ElementRef

  timeout: number

  ngAfterViewInit() {
    this.initTimeout()
  }

  initTimeout() {
    if (!this.message.sticky) {
      this.timeout = setTimeout(() => {
        // noinspection JSVoidFunctionReturnValueUsed,BadExpressionStatementJS
        this.onClose.emit({
          index: this.index,
          message: this.message,
        }) as unknown as number
      }, this.message.life || 3000) as unknown as number
    }
  }

  clearTimeout() {
    if (this.timeout) {
      clearTimeout(this.timeout)
      this.timeout = null
    }
  }

  onMouseEnter() {
    this.clearTimeout()
  }

  onMouseLeave() {
    this.initTimeout()
  }

  onCloseIconClick(event) {
    this.clearTimeout()

    this.onClose.emit({
      index: this.index,
      message: this.message,
    })

    event.preventDefault()
  }

  ngOnDestroy() {
    this.clearTimeout()
  }
}

@Component({
  selector: 'app-toast',
  template: `
    <div
      #container
      [ngClass]="{
        'p-toast p-component': true,
        'p-toast-top-right': position === 'top-right',
        'p-toast-top-left': position === 'top-left',
        'p-toast-bottom-right': position === 'bottom-right',
        'p-toast-bottom-left': position === 'bottom-left',
        'p-toast-top-center': position === 'top-center',
        'p-toast-bottom-center': position === 'bottom-center',
        'p-toast-center': position === 'center'
      }"
      [ngStyle]="style"
      [class]="styleClass"
    >
      <app-toast-item
        *ngFor="let msg of messages; let i = index"
        [message]="msg"
        [index]="i"
        (onClose)="onMessageClose($event)"
        [template]="template"
        @toastAnimation
        (@toastAnimation.start)="onAnimationStart($event)"
        [showTransitionOptions]="showTransitionOptions"
        [hideTransitionOptions]="hideTransitionOptions"
      ></app-toast-item>
    </div>
  `,
  animations: [trigger('toastAnimation', [transition(':enter, :leave', [query('@*', animateChild())])])],
})
export class ToastComponent implements OnInit, AfterContentInit, OnDestroy {
  @Input() key: string

  @Input() autoZIndex: boolean = true

  @Input() baseZIndex: number = 0

  @Input() style: unknown

  @Input() styleClass: string

  @Input() position: string = 'top-right'

  @Input() modal: boolean

  @Input() showTransitionOptions: string = '300ms ease-out'

  @Input() hideTransitionOptions: string = '250ms ease-in'

  @Output() onClose = new EventEmitter<{ message: Message }>()

  @ViewChild('container') containerViewChild: ElementRef

  @ContentChildren(PrimeTemplate) templates: QueryList<PrimeTemplate>

  messageSubscription: Subscription

  clearSubscription: Subscription

  removeSubscription: Subscription

  messages?: Message[]

  template: TemplateRef<{ $implicit: Message }>

  mask: HTMLDivElement

  constructor(
    public messageService: MessageService,
    private readonly documentRef: DocumentRefService,
  ) {}

  ngOnInit() {
    this.messageSubscription = this.messageService.messageObserver.subscribe((messages) => {
      if (messages) {
        const incomingMessagesFilteredByKey = messages.filter((m) => this.key === m.key)
        const incomingIds = incomingMessagesFilteredByKey.filter((m) => m.id).map((m) => m.id)
        this.messages = this.messages
          ? [...this.messages.filter((m) => !incomingIds.includes(m.id)), ...incomingMessagesFilteredByKey]
          : [...incomingMessagesFilteredByKey]

        if (this.modal && this.messages?.length) {
          this.enableModality()
        }
      }
    })

    this.clearSubscription = this.messageService.clearObserver.subscribe((key) => {
      if (key) {
        if (this.key === key) {
          this.messages = null
        }
      } else {
        this.messages = null
      }

      if (this.modal) {
        this.disableModality()
      }
    })

    this.removeSubscription = this.messageService.removeObserver.subscribe((id) => {
      if (this.messages) {
        this.messages = [...this.messages.filter((message) => message.id !== id)]
      }

      if (this.modal) {
        this.disableModality()
      }
    })
  }

  ngAfterContentInit() {
    this.templates.forEach((item) => {
      this.template = item.template
    })
  }

  onMessageClose(event) {
    this.messages.splice(event.index, 1)

    if (this.messages.length === 0) {
      this.disableModality()
    }

    this.onClose.emit({
      message: event.message,
    })
  }

  enableModality() {
    if (!this.mask) {
      this.mask = this.documentRef.document.createElement('div')
      this.mask.style.zIndex = String(parseInt(this.containerViewChild.nativeElement.style.zIndex, 10) - 1)
      const maskStyleClass = 'p-widget-overlay p-dialog-mask'
      DomHandler.addMultipleClasses(this.mask, maskStyleClass)
      this.documentRef.document.body.appendChild(this.mask)
    }
  }

  disableModality() {
    if (this.mask) {
      this.documentRef.document.body.removeChild(this.mask)
      this.mask = null
    }
  }

  onAnimationStart(event: AnimationEvent) {
    if (event.fromState === 'void' && this.autoZIndex) {
      this.containerViewChild.nativeElement.style.zIndex = String(this.baseZIndex + ++DomHandler.zindex)
    }
  }

  ngOnDestroy() {
    if (this.messageSubscription) {
      this.messageSubscription.unsubscribe()
    }

    if (this.clearSubscription) {
      this.clearSubscription.unsubscribe()
    }

    if (this.removeSubscription) {
      this.removeSubscription.unsubscribe()
    }

    this.disableModality()
  }
}

@NgModule({
  imports: [CommonModule, ButtonModule],
  exports: [ToastComponent, SharedModule],
  declarations: [ToastComponent, ToastItemComponent],
})
export class ToastModule {}
